/**
 * TrendBuy Shoppable Reels - JavaScript Controller (AJAX Optimized)
 * Instagram/TikTok-like vertical video shopping experience
 * * Features:
 * - Snap scroll with IntersectionObserver
 * - Autoplay/pause based on visibility
 * - Quick add to cart with drawer
 * - Social sharing
 * - View analytics tracking
 * - Keyboard navigation
 * - Touch gestures
 * - AJAX DOM Update Handling
 * - Volume Persistence Fixed
 */

(function() {
  'use strict';

  // ==========================================================================
  // Configuration
  // ==========================================================================
  
  const CONFIG = {
    observerThreshold: 0.7,        // Video must be 70% visible to play
    viewThreshold: 3,              // Seconds to count as a view
    preloadCount: 2,               // Number of videos to preload ahead
    toastDuration: 3000,           // Toast notification duration
    debounceDelay: 100,            // Scroll debounce delay
  };

  // ==========================================================================
  // State Management
  // ==========================================================================
  
  const state = {
    currentIndex: 0,
    totalReels: 0,
    isMuted: true,
    isPlaying: true,
    viewedReels: new Set(),
    watchTimes: new Map(),
    likedProducts: new Set(),
    currentShareUrl: '',
    currentShareTitle: '',
  };

  // ==========================================================================
  // DOM References
  // ==========================================================================
  
  let elements = {};

  function cacheElements() {
    elements = {
      container: document.getElementById('reels-container'),
      feed: document.getElementById('reels-feed'),
      loader: document.getElementById('reels-loader'),
      drawer: document.getElementById('reel-drawer'),
      shareModal: document.getElementById('reel-share-modal'),
      toast: document.getElementById('reel-toast'),
      navUp: document.querySelector('.reels-nav__btn--up'),
      navDown: document.querySelector('.reels-nav__btn--down'),
      navCurrent: document.querySelector('.reels-nav__current'),
      navTotal: document.querySelector('.reels-nav__total'),
      reelItems: document.querySelectorAll('.reel-item'),
    };

    state.totalReels = elements.reelItems.length;
  }

  // ==========================================================================
  // Video Management
  // ==========================================================================
  
  function initializeVideos() {
    elements.reelItems.forEach((reel, index) => {
      
      // PREVENT DUPLICATE INITIALIZATION ON AJAX LOAD
      if (reel.dataset.initialized === 'true') return;
      reel.dataset.initialized = 'true';

      const video = reel.querySelector('.reel-video');
      if (!video) return;

      if (index < CONFIG.preloadCount) {
        loadVideo(video);
      }

      video.addEventListener('loadeddata', () => {
        updateProgressBar(reel, video);
      });

      video.addEventListener('timeupdate', () => {
        updateProgressBar(reel, video);
        trackWatchTime(reel, video);
      });

      video.addEventListener('ended', () => {
        // Loop is handled by video attribute
      });

      video.addEventListener('error', (e) => {
        console.error('Video error:', e);
      });
    });
  }

  function loadVideo(video) {
    if (video.src || !video.dataset.src) return;
    video.src = video.dataset.src;
    video.load();
  }

  function updateProgressBar(reel, video) {
    const progressBar = reel.querySelector('.reel-progress__bar');
    if (!progressBar || !video.duration) return;
    
    const progress = (video.currentTime / video.duration) * 100;
    progressBar.style.width = `${progress}%`;
  }

  function trackWatchTime(reel, video) {
    const reelId = reel.dataset.productId;
    if (!reelId) return;

    const currentTime = Math.floor(video.currentTime);
    const previousTime = state.watchTimes.get(reelId) || 0;

    if (currentTime > previousTime) {
      state.watchTimes.set(reelId, currentTime);

      if (currentTime >= CONFIG.viewThreshold && !state.viewedReels.has(reelId)) {
        state.viewedReels.add(reelId);
        trackReelView(reel);
      }
    }
  }

  function trackReelView(reel) {
    const analyticsEl = reel.querySelector('.reel-analytics');
    if (!analyticsEl || analyticsEl.dataset.viewTracked === 'true') return;

    analyticsEl.dataset.viewTracked = 'true';

    const viewData = {
      event: 'reel_view',
      reel_id: reel.dataset.productId,
      product_handle: reel.dataset.productHandle,
      reel_index: reel.dataset.reelIndex,
      timestamp: new Date().toISOString(),
    };

    console.log('📊 Reel View:', viewData);
  }

  // ==========================================================================
  // Intersection Observer - Autoplay on Scroll
  // ==========================================================================
  
  let videoObserver;

  function initializeObserver() {
    const options = {
      root: elements.feed,
      rootMargin: '0px',
      threshold: CONFIG.observerThreshold,
    };

    videoObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const reel = entry.target;
        const video = reel.querySelector('.reel-video');
        
        const allItems = Array.from(elements.feed.querySelectorAll('.reel-item'));
        const index = allItems.indexOf(reel);

        if (entry.isIntersecting) {
          state.currentIndex = index;
          updateNavigation();

          loadVideo(video);
          preloadAdjacentVideos(index);

          if (state.isPlaying && video) {
            video.play().catch(() => {
              console.log('Autoplay blocked, waiting for interaction');
            });
          }

          updatePlayPauseButton(reel);
        } else {
          if (video) {
            video.pause();
            video.currentTime = 0;
          }
        }
      });
    }, options);

    elements.reelItems.forEach(reel => {
      videoObserver.observe(reel);
    });
  }

  function preloadAdjacentVideos(currentIndex) {
    for (let i = 1; i <= CONFIG.preloadCount; i++) {
      const nextIndex = currentIndex + i;
      const prevIndex = currentIndex - i;

      if (nextIndex < state.totalReels) {
        const nextVideo = elements.reelItems[nextIndex]?.querySelector('.reel-video');
        if (nextVideo) loadVideo(nextVideo);
      }

      if (prevIndex >= 0) {
        const prevVideo = elements.reelItems[prevIndex]?.querySelector('.reel-video');
        if (prevVideo) loadVideo(prevVideo);
      }
    }
  }

  // ==========================================================================
  // Navigation
  // ==========================================================================
  
  function updateNavigation() {
    if (elements.navCurrent) {
      elements.navCurrent.textContent = state.currentIndex + 1;
    }
    if (elements.navUp) {
      elements.navUp.disabled = false;
    }
    if (elements.navDown) {
      elements.navDown.disabled = false;
    }
  }

  function navigateToReel(index) {
    if (index < 0 || index >= state.totalReels) return;

    const targetReel = elements.reelItems[index];
    if (targetReel) {
      targetReel.scrollIntoView({ behavior: 'smooth' });
    }
  }

  function navigateUp() {
    if (state.currentIndex <= 0) {
      navigateToReel(state.totalReels - 1);
    } else {
      navigateToReel(state.currentIndex - 1);
    }
  }

  function navigateDown() {
    if (state.currentIndex >= state.totalReels - 1) {
      navigateToReel(0);
    } else {
      navigateToReel(state.currentIndex + 1);
    }
  }

  // ==========================================================================
  // Video Controls
  // ==========================================================================
  
  function updatePlayPauseButton(reel) {
    const btn = reel.querySelector('.reel-control--play-pause');
    if (!btn) return;

    if (state.isPlaying) {
      btn.classList.remove('paused');
    } else {
      btn.classList.add('paused');
    }
  }

  function togglePlayPause(reel) {
    const video = reel.querySelector('.reel-video');
    if (!video) return;

    if (video.paused) {
      video.play();
      state.isPlaying = true;
    } else {
      video.pause();
      state.isPlaying = false;
    }

    updatePlayPauseButton(reel);
  }

  function toggleMute() {
    state.isMuted = !state.isMuted;

    elements.reelItems.forEach(reel => {
      const video = reel.querySelector('.reel-video');
      const muteBtn = reel.querySelector('.reel-control--mute');

      if (video) {
        video.muted = state.isMuted;
      }

      if (muteBtn) {
        muteBtn.classList.toggle('muted', state.isMuted);
      }
    });
  }

  // ==========================================================================
  // Quick Add Drawer
  // ==========================================================================
  
  let currentDrawerProduct = null;

  function openDrawer(reel) {
    const productId = reel.dataset.productId;
    const variantId = reel.dataset.variantId;
    const productUrl = reel.dataset.productUrl;

    fetch(`${productUrl}.js`)
      .then(res => res.json())
      .then(product => {
        currentDrawerProduct = product;
        populateDrawer(product, variantId);
        elements.drawer.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
      })
      .catch(err => {
        console.error('Error fetching product:', err);
        showToast('Failed to load product', 'error');
      });
  }

  function populateDrawer(product, selectedVariantId) {
    const drawerImage = elements.drawer.querySelector('.reel-drawer__image');
    if (drawerImage && product.featured_image) {
      drawerImage.src = product.featured_image;
      drawerImage.alt = product.title;
    }

    const drawerTitle = elements.drawer.querySelector('.reel-drawer__title');
    if (drawerTitle) {
      drawerTitle.textContent = product.title;
    }

    const selectedVariant = product.variants.find(v => v.id == selectedVariantId) || product.variants[0];
    updateDrawerPrice(selectedVariant);

    const variantsContainer = document.getElementById('drawer-variants');
    if (variantsContainer) {
      variantsContainer.innerHTML = '';

      if (product.options.length > 0 && product.options[0].name !== 'Title') {
        product.options.forEach((option, optionIndex) => {
          const optionValues = [...new Set(product.variants.map(v => v.options[optionIndex]))];
          
          const groupDiv = document.createElement('div');
          groupDiv.className = 'reel-drawer__variant-group';
          
          const label = document.createElement('span');
          label.className = 'reel-drawer__variant-label';
          label.textContent = option.name;
          groupDiv.appendChild(label);

          const optionsDiv = document.createElement('div');
          optionsDiv.className = 'reel-drawer__variant-options';

          optionValues.forEach(value => {
            const btn = document.createElement('button');
            btn.className = 'reel-drawer__variant-btn';
            btn.textContent = value;
            btn.dataset.option = optionIndex;
            btn.dataset.value = value;

            if (selectedVariant.options[optionIndex] === value) {
              btn.classList.add('selected');
            }

            btn.addEventListener('click', () => handleVariantSelect(btn, product));
            optionsDiv.appendChild(btn);
          });

          groupDiv.appendChild(optionsDiv);
          variantsContainer.appendChild(groupDiv);
        });
      }
    }

    const viewLink = document.getElementById('drawer-view-link');
    if (viewLink) {
      viewLink.href = `/products/${product.handle}`;
    }

    const qtyInput = document.getElementById('drawer-quantity');
    if (qtyInput) {
      qtyInput.value = 1;
    }

    updateAddButtonState(selectedVariant);
  }

  function handleVariantSelect(btn, product) {
    const siblings = btn.parentElement.querySelectorAll('.reel-drawer__variant-btn');
    siblings.forEach(s => s.classList.remove('selected'));
    btn.classList.add('selected');

    const selectedOptions = [];
    document.querySelectorAll('.reel-drawer__variant-group').forEach((group, idx) => {
      const selectedBtn = group.querySelector('.reel-drawer__variant-btn.selected');
      if (selectedBtn) {
        selectedOptions[idx] = selectedBtn.dataset.value;
      }
    });

    const matchingVariant = product.variants.find(v => {
      return selectedOptions.every((opt, idx) => v.options[idx] === opt);
    });

    if (matchingVariant) {
      updateDrawerPrice(matchingVariant);
      updateAddButtonState(matchingVariant);
    }
  }

  function updateDrawerPrice(variant) {
    const priceEl = elements.drawer.querySelector('.reel-drawer__price');
    const addPriceEl = elements.drawer.querySelector('.reel-drawer__add-price');

    if (priceEl) {
      priceEl.textContent = formatMoney(variant.price);
    }

    if (addPriceEl) {
      addPriceEl.textContent = `• ${formatMoney(variant.price)}`;
    }
  }

  function updateAddButtonState(variant) {
    const addBtn = document.getElementById('drawer-add-btn');
    if (!addBtn) return;

    addBtn.disabled = !variant.available;
    addBtn.dataset.variantId = variant.id;

    const textEl = addBtn.querySelector('.reel-drawer__add-text');
    if (textEl) {
      textEl.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
    }
  }

  function closeDrawer() {
    elements.drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    currentDrawerProduct = null;
  }

  // ==========================================================================
  // Cart Functions
  // ==========================================================================
  
  function addToCart(variantId, quantity = 1) {
    const addBtn = document.getElementById('drawer-add-btn');
    if (addBtn) {
      addBtn.classList.add('loading');
    }

    const formData = {
      items: [{
        id: parseInt(variantId),
        quantity: parseInt(quantity)
      }]
    };

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData)
    })
    .then(res => {
      if (!res.ok) throw new Error('Add to cart failed');
      return res.json();
    })
    .then(data => {
      showToast('Added to cart! 🛒', 'success');
      closeDrawer();
      updateCartCount();
      
      console.log('🛒 Added to cart:', {
        variant_id: variantId,
        quantity: quantity,
        product: currentDrawerProduct?.title
      });
    })
    .catch(err => {
      console.error('Cart error:', err);
      showToast('Failed to add to cart', 'error');
    })
    .finally(() => {
      if (addBtn) {
        addBtn.classList.remove('loading');
      }
    });
  }

  function quickAddToCart(btn) {
    const available = btn.dataset.available === 'true';
    if (!available) {
      showToast('This item is sold out', 'error');
      return;
    }

    const reel = btn.closest('.reel-item');
    if (reel) {
      openDrawer(reel);
    }
  }

  function updateCartCount() {
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        const cartCounts = document.querySelectorAll('.cart-count, [data-cart-count]');
        cartCounts.forEach(el => {
          el.textContent = cart.item_count;
        });
      })
      .catch(() => {});
  }

  // ==========================================================================
  // Share Functions
  // ==========================================================================
  
  function openShareModal(url, title) {
    state.currentShareUrl = url;
    state.currentShareTitle = title;
    elements.shareModal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function closeShareModal() {
    elements.shareModal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    
    const copiedMsg = document.getElementById('share-copied-msg');
    if (copiedMsg) {
      copiedMsg.classList.remove('visible');
    }
  }

  function handleShare(platform) {
    const url = encodeURIComponent(state.currentShareUrl);
    const title = encodeURIComponent(state.currentShareTitle);
    let shareUrl;

    switch (platform) {
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${title}%20${url}`;
        window.open(shareUrl, '_blank');
        break;

      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        window.open(shareUrl, '_blank', 'width=600,height=400');
        break;

      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${title}&url=${url}`;
        window.open(shareUrl, '_blank', 'width=600,height=400');
        break;

      case 'copy':
        navigator.clipboard.writeText(state.currentShareUrl).then(() => {
          const copiedMsg = document.getElementById('share-copied-msg');
          if (copiedMsg) {
            copiedMsg.classList.add('visible');
            setTimeout(() => copiedMsg.classList.remove('visible'), 2000);
          }
        }).catch(() => {
          showToast('Failed to copy link', 'error');
        });
        break;

      default:
        if (navigator.share) {
          navigator.share({
            title: state.currentShareTitle,
            url: state.currentShareUrl
          }).catch(() => {});
        }
    }
    console.log('📤 Share:', { platform, url: state.currentShareUrl });
  }

  // ==========================================================================
  // Like/Wishlist Functions
  // ==========================================================================
  
  function toggleLike(btn) {
    const productId = btn.dataset.productId;
    const isLiked = state.likedProducts.has(productId);

    if (isLiked) {
      state.likedProducts.delete(productId);
      btn.classList.remove('liked');
    } else {
      state.likedProducts.add(productId);
      btn.classList.add('liked');
      showToast('Added to wishlist ❤️', 'success');
    }

    localStorage.setItem('reels_liked', JSON.stringify([...state.likedProducts]));
  }

  function loadLikedProducts() {
    try {
      const saved = localStorage.getItem('reels_liked');
      if (saved) {
        const liked = JSON.parse(saved);
        liked.forEach(id => state.likedProducts.add(id));

        elements.reelItems.forEach(reel => {
          const productId = reel.dataset.productId;
          const likeBtn = reel.querySelector('.reel-action--like');
          if (likeBtn && state.likedProducts.has(productId)) {
            likeBtn.classList.add('liked');
          }
        });
      }
    } catch (e) {
      console.error('Error loading liked products:', e);
    }
  }

  // ==========================================================================
  // Utility Functions
  // ==========================================================================
  
  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return `₹${amount}`; 
  }

  function showToast(message, type = '') {
    if (!elements.toast) return;

    elements.toast.textContent = message;
    elements.toast.className = `reel-toast visible ${type}`;

    setTimeout(() => {
      elements.toast.classList.remove('visible');
    }, CONFIG.toastDuration);
  }

  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // ==========================================================================
  // Event Listeners
  // ==========================================================================
  
  function bindEvents() {
    if (elements.navUp) {
      elements.navUp.addEventListener('click', navigateUp);
    }
    if (elements.navDown) {
      elements.navDown.addEventListener('click', navigateDown);
    }

    document.addEventListener('keydown', (e) => {
      if (!elements.container) return;

      switch (e.key) {
        case 'ArrowUp':
        case 'k':
          e.preventDefault();
          navigateUp();
          break;
        case 'ArrowDown':
        case 'j':
          e.preventDefault();
          navigateDown();
          break;
        case 'm':
          toggleMute();
          break;
        case ' ':
          e.preventDefault();
          const currentReel = elements.reelItems[state.currentIndex];
          if (currentReel) togglePlayPause(currentReel);
          break;
        case 'Escape':
          if (elements.drawer.getAttribute('aria-hidden') === 'false') {
            closeDrawer();
          } else if (elements.shareModal.getAttribute('aria-hidden') === 'false') {
            closeShareModal();
          }
          break;
      }
    });

    elements.feed.addEventListener('click', (e) => {
      const reel = e.target.closest('.reel-item');
      if (!reel) return;

      if (e.target.closest('.reel-control--play-pause')) {
        togglePlayPause(reel);
        return;
      }

      if (e.target.closest('.reel-control--mute')) {
        toggleMute();
        return;
      }

      if (e.target.closest('.reel-tap-overlay')) {
        togglePlayPause(reel);
        return;
      }

      if (e.target.closest('.reel-action--like')) {
        toggleLike(e.target.closest('.reel-action--like'));
        return;
      }

      if (e.target.closest('.reel-action--share')) {
        const btn = e.target.closest('.reel-action--share');
        openShareModal(btn.dataset.productUrl, btn.dataset.productTitle);
        return;
      }

      if (e.target.closest('.reel-action--cart')) {
        quickAddToCart(e.target.closest('.reel-action--cart'));
        return;
      }

      if (e.target.closest('.reel-product-card__buy')) {
        e.preventDefault();
        openDrawer(reel);
        return;
      }
    });

    elements.drawer.addEventListener('click', (e) => {
      if (e.target.closest('.reel-drawer__close') || e.target.classList.contains('reel-drawer__overlay')) {
        closeDrawer();
        return;
      }

      if (e.target.closest('.reel-drawer__qty-btn')) {
        const btn = e.target.closest('.reel-drawer__qty-btn');
        const input = document.getElementById('drawer-quantity');
        const action = btn.dataset.action;
        let value = parseInt(input.value) || 1;

        if (action === 'increase') {
          value = Math.min(value + 1, 99);
        } else if (action === 'decrease') {
          value = Math.max(value - 1, 1);
        }

        input.value = value;
        return;
      }

      if (e.target.closest('#drawer-add-btn')) {
        const btn = document.getElementById('drawer-add-btn');
        const variantId = btn.dataset.variantId;
        const quantity = document.getElementById('drawer-quantity').value;
        addToCart(variantId, quantity);
        return;
      }
    });

    elements.shareModal.addEventListener('click', (e) => {
      if (e.target.closest('.reel-share-modal__close') || e.target.classList.contains('reel-share-modal__overlay')) {
        closeShareModal();
        return;
      }

      if (e.target.closest('.reel-share-option')) {
        const btn = e.target.closest('.reel-share-option');
        handleShare(btn.dataset.share);
        return;
      }
    });

    let touchStartY = 0;
    let touchEndY = 0;

    elements.feed.addEventListener('touchstart', (e) => {
      touchStartY = e.changedTouches[0].screenY;
    }, { passive: true });

    elements.feed.addEventListener('touchend', (e) => {
      touchEndY = e.changedTouches[0].screenY;
      handleSwipe();
    }, { passive: true });

    function handleSwipe() {
      const swipeThreshold = 50;
      const diff = touchStartY - touchEndY;
      if (Math.abs(diff) < swipeThreshold) return;
    }

    document.addEventListener('visibilitychange', () => {
      const currentReel = elements.reelItems[state.currentIndex];
      const video = currentReel?.querySelector('.reel-video');

      if (document.hidden) {
        if (video) video.pause();
      } else {
        if (video && state.isPlaying) video.play().catch(() => {});
      }
    });
  }

  // ==========================================================================
  // AJAX Event Listener (NEW + VOLUME PERSISTENCE)
  // ==========================================================================
  
  document.addEventListener('reels:updated', function() {
    elements.reelItems = document.querySelectorAll('.reel-item');
    state.totalReels = elements.reelItems.length;

    initializeVideos();
    loadLikedProducts();

    elements.reelItems.forEach(reel => {
      const video = reel.querySelector('.reel-video');
      const muteBtn = reel.querySelector('.reel-control--mute');
      
      if (video) {
        video.muted = state.isMuted;
      }
      if (muteBtn) {
        muteBtn.classList.toggle('muted', state.isMuted);
      }

      if (videoObserver) {
        videoObserver.observe(reel);
      }
    });
  });

  // ==========================================================================
  // Initialization
  // ==========================================================================
  
  function init() {
    cacheElements();

    if (state.totalReels === 0) {
      if (elements.loader) {
        elements.loader.classList.add('hidden');
      }
      return;
    }

    initializeVideos();
    initializeObserver();
    bindEvents();
    loadLikedProducts();
    updateNavigation();

    setTimeout(() => {
      if (elements.loader) {
        elements.loader.classList.add('hidden');
      }
    }, 500);

    if (elements.reelItems[0]) {
      elements.reelItems[0].scrollIntoView();
    }

    console.log('🎬 TrendBuy Reels initialized with', state.totalReels, 'reels');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();